import { useRouter } from 'next/router';
import cn from 'classnames';
// Types
import {
  GetAthleteStreams_liveAndUpcomingStreams_entities,
  GetAthleteStreams_pastStreams_entities,
} from 'api/streams/types/GetAthleteStreams';
// Hooks
import {
  useGetCurrUser,
  useBreakpoints,
  useStreamPurchaseNavigation,
} from 'hooks';
// Helpers
import { getStreamDateV2 } from 'helpers/streams';
// Ui
import LinkButton from 'ui3/LinkButton/LinkButton';
import Text from 'ui3/Text/Text';
import EllipsisText from 'ui3/EllipsisText/EllipsisText';
import ContentCard from 'ui3/Card/ContentCard';
// Styles
import styles from './LiveStream.module.scss';

type Stream =
  | GetAthleteStreams_liveAndUpcomingStreams_entities
  | GetAthleteStreams_pastStreams_entities;

type LiveStreamProps = {
  stream?: Stream;
  loading?: boolean;
  className?: string;
  onLinkClick?: (
    data: Stream,
    e: React.SyntheticEvent<HTMLAnchorElement>
  ) => void;
};

const LiveStream = ({ stream, className }: LiveStreamProps) => {
  const { push } = useRouter();
  const { data: currentUser } = useGetCurrUser();
  const { isBreakpointL } = useBreakpoints();
  const {
    navigateToStream,
    getStreamNavigationProps,
  } = useStreamPurchaseNavigation();

  if (!stream) {
    return null;
  }

  const streamLink = getStreamNavigationProps(stream).linkUrl;
  const streamDate = getStreamDateV2(stream?.scheduleDateUtc);
  const hasAccessToWatch =
    stream.isPurchased ||
    stream.isFree ||
    currentUser?.me.id === stream.store?.id;
  const liveStreamActionButton =
    !stream.isPurchased && !stream.isFree ? 'Buy now' : 'Watch now';
  const handleReadMoreClick = () => {
    push(streamLink);
  };

  const handleActionClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigateToStream(stream, hasAccessToWatch);
  };

  return (
    <section className={cn(styles.root, className)}>
      <div className={cn(styles.header, styles.noPadding)}>
        <Text variant="h5">Live Now</Text>
      </div>
      <div className={styles.mainBlock}>
        <ContentCard
          className={styles.contentCard}
          type={stream.type}
          status={stream.streamStatus}
          imageUrl={stream?.imageURL || ''}
          // Auto-freed PPV keeps a non-zero `price`, so branch on `isFree`.
          price={
            stream?.isFree || stream?.price === 0
              ? 'free'
              : `$${stream?.price.toString()}`
          }
          size="large"
        />

        <div className={styles.body}>
          <Text variant={isBreakpointL ? 'h3' : 'h6'} className={styles.title}>
            {stream.name}
          </Text>
          <Text
            variant="body2Regular"
            color="lights-low"
            className={cn(styles.date, styles.noPadding)}
          >
            {streamDate}
          </Text>
          <EllipsisText
            text={stream.description}
            className={styles.description}
            maxLine={{ xs: 2, ml: 3 }}
            isGold
            customMoreText="Read More"
            onReadMoreClick={handleReadMoreClick}
          />
          <LinkButton
            className={styles.linkButton}
            variant="secondary"
            icon="playSolid"
            iconPosition="right"
            iconSize={16}
            link={getStreamNavigationProps(stream, !hasAccessToWatch).linkUrl}
            onClick={handleActionClick}
          >
            {liveStreamActionButton}
          </LinkButton>
        </div>
      </div>
    </section>
  );
};

export default LiveStream;
